import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator, NamingConvention } from "namirasoft-core";
import { AlertInputRow, AlertRow, AlertTriggerAggregator, AlertTriggerOperator, EnvironmentRow, KubernetesRow, ProjectRow, ServerRow } from "namirasoft-infra";
import { LogGroupRow } from "namirasoft-log";
import { TopicRow } from "namirasoft-notification-sender";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { InfraEntities } from "../InfraEntities";
import { InfraLayout } from '../InfraLayout';
import { InfraRouterProps } from "../InfraRouterProps";

interface AlertEditPageProps extends NSARouterMakerResult, InfraRouterProps
{
    isEdit: boolean;
}

interface AlertEditPageState
{
    selected_project_id?: string;
    selected_environment_id?: string;
    metric_name?: string;
    sample_size?: number;
    aggregator?: AlertTriggerAggregator;
    operator?: AlertTriggerOperator;
    threshold?: number;
}

export default class AlertEditPage extends Component<AlertEditPageProps, AlertEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxEntity_ProjectID = createRef<NSBoxEntity<ProjectRow>>();
    NSBoxEntity_EnvironmentID = createRef<NSBoxEntity<EnvironmentRow>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxEntity_ServerIDs = createRef<NSBoxEntity<ServerRow>>();
    NSBoxEntity_KubernetesIDs = createRef<NSBoxEntity<KubernetesRow>>();
    NSBoxEntity_TopicID = createRef<NSBoxEntity<TopicRow>>();
    NSBoxString_MetricName = createRef<NSBoxString>();
    NSBoxInteger_TriggerSampleSize = createRef<NSBoxInteger>();
    NSBoxEnum_TriggerAggregator = createRef<NSBoxEnum<AlertTriggerAggregator>>();
    NSBoxEnum_TriggerOperator = createRef<NSBoxEnum<AlertTriggerOperator>>();
    NSBoxString_Threshold = createRef<NSBoxInteger>();
    NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: AlertEditPageProps)
    {
        super(props);
        this.state = {};
    }

    getMetricMessage()
    {
        let aggregator_map = {
            [AlertTriggerAggregator.Min]: "minimum",
            [AlertTriggerAggregator.Max]: "maximum",
            [AlertTriggerAggregator.Avg]: "average",
            [AlertTriggerAggregator.Sum]: "sum",
        }
        let operator_map = {
            [AlertTriggerOperator.Equal]: "equal to",
            [AlertTriggerOperator.NotEqual]: "not equal to",
            [AlertTriggerOperator.MoreThan]: "greater than",
            [AlertTriggerOperator.LessThan]: "less than",
            [AlertTriggerOperator.LessThanOrEqual]: "less than or equal to",
            [AlertTriggerOperator.MoreThanOrEqual]: "greater than or equal to",
        }

        let { metric_name, sample_size, aggregator, operator, threshold } = this.state;

        if (metric_name && sample_size && aggregator && operator && threshold)
            return `Trigger if the ${aggregator_map[aggregator]} of ${metric_name} over the last ${sample_size} sample${sample_size > 1 ? "s" : ""} is ${operator_map[operator]} ${threshold}.`;
        else
            return "";
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let metric_message = this.getMetricMessage();

        return (
            <InfraLayout<AlertRow, AlertInputRow>
                {...this.props}
                pages={{
                    entity: InfraEntities.alert(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): AlertInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let project_id = this.NSBoxEntity_ProjectID.current?.getValueOne() ?? "";
                                        let environment_id = this.NSBoxEntity_EnvironmentID.current?.getValueOne() ?? "";
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let server_ids = this.NSBoxEntity_ServerIDs.current?.getValueAll() ?? null;
                                        let kubernetes_ids = this.NSBoxEntity_KubernetesIDs.current?.getValueAll() ?? null;
                                        let topic_id = this.NSBoxEntity_TopicID.current?.getValueOne() ?? "";
                                        let metric_name = this.NSBoxString_MetricName.current?.getValue() ?? "";
                                        let trigger_sample_size = this.NSBoxInteger_TriggerSampleSize.current?.getValue() ?? 0;
                                        let trigger_aggregator = this.NSBoxEnum_TriggerAggregator.current?.getValueEnumOne(AlertTriggerAggregator, AlertTriggerAggregator.Avg) ?? AlertTriggerAggregator.Avg;
                                        let trigger_operator = this.NSBoxEnum_TriggerOperator.current?.getValueEnumOne(AlertTriggerOperator, AlertTriggerOperator.Equal) ?? AlertTriggerOperator.Equal;
                                        let threshold = this.NSBoxString_Threshold.current?.getValue() ?? 0;
                                        let description = this.NSBoxTextArea_Description.current?.getValue() ?? null;

                                        return {
                                            workspace_id: "",
                                            name,
                                            project_id,
                                            environment_id,
                                            log_group_id,
                                            server_ids,
                                            kubernetes_ids,
                                            topic_id,
                                            metric_name,
                                            trigger_sample_size,
                                            trigger_aggregator,
                                            trigger_operator,
                                            threshold,
                                            description,
                                        };
                                    },
                                    setRow: (row: AlertRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEntity_ProjectID.current?.setValueOne(row.project_id);
                                        this.NSBoxEntity_LogGroupID.current?.setValueOne(row.log_group_id);
                                        this.NSBoxEntity_TopicID.current?.setValueOne(row.topic_id);
                                        this.NSBoxString_MetricName.current?.setValue(row.metric_name);
                                        this.NSBoxInteger_TriggerSampleSize.current?.setValue(row.trigger_sample_size);
                                        this.NSBoxEnum_TriggerAggregator.current?.setValueOne(row.trigger_aggregator);
                                        this.NSBoxEnum_TriggerOperator.current?.setValueOne(row.trigger_operator);
                                        this.NSBoxString_Threshold.current?.setValue(row.threshold);
                                        this.NSBoxTextArea_Description.current?.setValue(row.description);

                                        this.setState(
                                            () => ({
                                                selected_project_id: row.project_id,
                                                selected_environment_id: row.environment_id,
                                                metric_name: row.metric_name,
                                                sample_size: row.trigger_sample_size,
                                                aggregator: row.trigger_aggregator,
                                                operator: row.trigger_operator,
                                                threshold: row.threshold,
                                            }),
                                            () =>
                                            {
                                                this.NSBoxEntity_EnvironmentID.current?.setValueOne(row.environment_id);
                                                this.NSBoxEntity_ServerIDs.current?.setValueAll(row.server_ids);
                                                this.NSBoxEntity_KubernetesIDs.current?.setValueAll(row.kubernetes_ids);
                                            }
                                        );
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Metric",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxString
                                                        ref={this.NSBoxString_MetricName}
                                                        required
                                                        title="Metric Name"
                                                        onChanged={(box) => this.setState(() => ({ metric_name: box.getValue(false) ?? "" }))}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    <NSRow>
                                                        <NSBoxInteger
                                                            ref={this.NSBoxInteger_TriggerSampleSize}
                                                            required
                                                            title="Trigger Sample Size"
                                                            onChanged={(box) => this.setState(() => ({ sample_size: box.getValue(false) ?? 0 }))}
                                                        />
                                                        <NSBoxEnum<AlertTriggerAggregator>
                                                            ref={this.NSBoxEnum_TriggerAggregator}
                                                            title="Trigger Aggregator"
                                                            required
                                                            multiple={false}
                                                            getEnumObject={() => AlertTriggerAggregator}
                                                            onChanged={(box) => this.setState(() => ({ aggregator: box.getValueOne() as AlertTriggerAggregator }))}
                                                        />
                                                    </NSRow>
                                                    <NSRow>
                                                        <NSBoxEnum<AlertTriggerOperator>
                                                            ref={this.NSBoxEnum_TriggerOperator}
                                                            title="Trigger Operator"
                                                            required
                                                            multiple={false}
                                                            getEnumObject={() => AlertTriggerOperator}
                                                            getTitle={(item) => NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)}
                                                            onChanged={(box) => this.setState(() => ({ operator: box.getValueOne() as AlertTriggerOperator }))}
                                                        />
                                                        <NSBoxInteger
                                                            ref={this.NSBoxString_Threshold}
                                                            required
                                                            title="Threshold"
                                                            onChanged={(box) => this.setState(() => ({ threshold: box.getValue(false) ?? 0 }))}
                                                        />
                                                    </NSRow>
                                                    {
                                                        metric_message &&
                                                        <p className="w-100 mb-0 small text-dark" style={{ maxWidth: NSBox.width.double }}>
                                                            {metric_message}
                                                        </p>
                                                    }
                                                </NSColumn>
                                            )
                                        },
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ProjectRow>
                        ref={this.NSBoxEntity_ProjectID}
                        title="Project"
                        required
                        multiple={false}
                        table_name="project"
                        table={namirasoft.infra.tables.project}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_project_id: box.getValueOne() ?? undefined }),
                                () =>
                                {
                                    this.NSBoxEntity_EnvironmentID.current?.setValue(null);
                                    this.NSBoxEntity_EnvironmentID.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<EnvironmentRow>
                        ref={this.NSBoxEntity_EnvironmentID}
                        title="Environment"
                        required
                        multiple={false}
                        table_name="environment"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_project_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.environment,
                                    namirasoft.infra.tables.environment.columns.project_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_project_id,
                                ));

                            return await this.props.servers.infra.environment.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        onChanged={(box) =>
                        {
                            this.setState(
                                () => ({ selected_environment_id: box.getValueOne(false) ?? "" }),
                                () =>
                                {
                                    this.NSBoxEntity_ServerIDs.current?.setValue(null);
                                    this.NSBoxEntity_KubernetesIDs.current?.setValue(null);
                                    this.NSBoxEntity_ServerIDs.current?.reload();
                                    this.NSBoxEntity_KubernetesIDs.current?.reload();
                                },
                            )
                        }}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<ServerRow>
                        ref={this.NSBoxEntity_ServerIDs}
                        title="Servers"
                        required={false}
                        multiple
                        table_name="server"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_environment_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.server,
                                    namirasoft.infra.tables.server.columns.environment_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_environment_id,
                                ));

                            return await this.props.servers.infra.server.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<KubernetesRow>
                        ref={this.NSBoxEntity_KubernetesIDs}
                        title="Kubernetes"
                        required={false}
                        multiple
                        table_name="kubernetes"
                        list={async (filters, page, size, sorts) =>
                        {
                            if (!filters)
                                filters = [];

                            if (this.state.selected_environment_id)
                                filters.push(new FilterItem(
                                    namirasoft.infra.tables.kubernetes,
                                    namirasoft.infra.tables.kubernetes.columns.environment_id,
                                    false,
                                    FilterItemOperator.all.equals,
                                    this.state.selected_environment_id,
                                ));

                            return await this.props.servers.infra.kubernetes.List(filters, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        table_name="log-group"
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<TopicRow>
                        ref={this.NSBoxEntity_TopicID}
                        title="Topic"
                        required={false}
                        multiple
                        table_name="topic"
                        table={namirasoft.notification_sender.tables.topic}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                    />
                </NSColumn>
            </ InfraLayout>
        );
    }
}